import type { CopyRecipe, CopyRules, DriveItem, FileCategory } from '../types'
import { defaultRules, emptyCategories } from '../types'

export const recipes: CopyRecipe[] = [
  { id: 'all-drive', eyebrow: 'La copia esencial', title: 'Todo mi Drive', description: 'Guarda tus archivos propios y compartidos en tu carpeta local.' },
  { id: 'media-2y', eyebrow: 'La receta más usada', title: 'Multimedia de más de 2 años', description: 'Fotos, vídeos y audio antiguos; organizado para volver a encontrarlos.' },
  { id: 'work-docs', eyebrow: 'Archivo de trabajo', title: 'Documentos de trabajo', description: 'Documentos, hojas, presentaciones y PDF en un único lugar seguro.' },
  { id: 'custom', eyebrow: 'Control total', title: 'Copia a medida', description: 'Empieza con una regla vacía y ajusta cada detalle.' },
]

const GOOGLE_MIME = 'application/vnd.google-apps.'
const imageMimes = new Set(['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif', 'image/tiff', 'image/bmp'])
const videoMimes = new Set(['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/x-matroska', 'video/webm', 'video/mpeg'])
const audioMimes = new Set(['audio/mpeg', 'audio/mp4', 'audio/wav', 'audio/x-wav', 'audio/ogg', 'audio/flac', 'audio/aac'])

export function categoryFor(file: DriveItem): FileCategory {
  const mime = file.mimeType
  if (mime === `${GOOGLE_MIME}document`) return 'documents'
  if (mime === `${GOOGLE_MIME}spreadsheet`) return 'sheets'
  if (mime === `${GOOGLE_MIME}presentation`) return 'presentations'
  if (mime === 'application/pdf') return 'pdf'
  if (imageMimes.has(mime) || mime.startsWith('image/')) return 'images'
  if (videoMimes.has(mime) || mime.startsWith('video/')) return 'videos'
  if (audioMimes.has(mime) || mime.startsWith('audio/')) return 'audio'

  const extension = file.name.split('.').pop()?.toLowerCase() ?? ''
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif', 'tif', 'tiff', 'bmp', 'raw', 'dng'].includes(extension)) return 'images'
  if (['mp4', 'mov', 'avi', 'mkv', 'webm', 'm4v', 'mpeg', 'mpg'].includes(extension)) return 'videos'
  if (['mp3', 'wav', 'm4a', 'flac', 'ogg', 'aac'].includes(extension)) return 'audio'
  if (['pdf'].includes(extension)) return 'pdf'
  if (['xls', 'xlsx', 'csv', 'ods'].includes(extension)) return 'sheets'
  if (['ppt', 'pptx', 'odp'].includes(extension)) return 'presentations'
  if (['doc', 'docx', 'odt', 'rtf', 'txt', 'md'].includes(extension)) return 'documents'
  return 'other'
}

export function rulesForRecipe(recipeId: CopyRecipe['id']): CopyRules {
  const rules = defaultRules()
  if (recipeId === 'media-2y') {
    rules.categories = { ...emptyCategories(), images: true, videos: true, audio: true, documents: false, sheets: false, presentations: false, pdf: false, other: false }
    rules.age = '2y'
    rules.organization = 'year-month-type'
  }
  if (recipeId === 'work-docs') {
    rules.categories = { ...emptyCategories(), images: false, videos: false, audio: false, documents: true, sheets: true, presentations: true, pdf: true, other: false }
    rules.ownership = 'mine'
    rules.organization = 'year-type'
  }
  if (recipeId === 'custom') {
    rules.categories = { ...emptyCategories(), images: false, videos: false, audio: false, documents: false, sheets: false, presentations: false, pdf: false, other: false }
  }
  return rules
}

function normalise(text: string) {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
}

export function interpretRule(text: string, current: CopyRules): CopyRules {
  const next: CopyRules = { ...current, categories: { ...current.categories } }
  const value = normalise(text)
  if (/(multimedia|fotos|imagenes|imagenes|video|videos|audio|musica)/.test(value)) {
    next.categories = { ...emptyCategories(), images: true, videos: true, audio: true, documents: false, sheets: false, presentations: false, pdf: false, other: false }
  }
  if (/(documentos|trabajo|pdf|hojas|presentaciones)/.test(value)) {
    next.categories = { ...emptyCategories(), images: false, videos: false, audio: false, documents: true, sheets: true, presentations: true, pdf: true, other: false }
  }
  const ageMatch = value.match(/(?:mas de|mayor(?:es)? de|anteriores? a)\s*(\d+)\s*(ano|anos|mes|meses)/)
  if (ageMatch) {
    const amount = Number(ageMatch[1])
    const unit = ageMatch[2]
    if ((unit.startsWith('mes') && amount === 6) || (!unit.startsWith('mes') && [1, 2, 5].includes(amount))) {
      next.age = unit.startsWith('mes') ? '6m' : `${amount}y` as CopyRules['age']
      next.after = undefined
      next.before = undefined
    } else {
      const limit = new Date()
      if (unit.startsWith('mes')) limit.setMonth(limit.getMonth() - amount)
      else limit.setFullYear(limit.getFullYear() - amount)
      next.age = 'custom'
      next.after = undefined
      next.before = limit.toISOString().slice(0, 10)
    }
  }
  const sizeMatch = value.match(/(?:mas de|mayor(?:es)? de)\s*(\d+(?:[.,]\d+)?)\s*(gb|mb)/)
  if (sizeMatch) {
    const raw = Number(sizeMatch[1].replace(',', '.'))
    next.minSizeMb = sizeMatch[2] === 'gb' ? raw * 1024 : raw
  }
  if (value.includes('solo mios') || value.includes('solo propios')) next.ownership = 'mine'
  if (value.includes('compartid')) next.ownership = 'shared'
  if (value.includes('por mes')) next.organization = 'year-month-type'
  if (value.includes('por ano') || value.includes('por año')) next.organization = 'year-type'
  return next
}

function ageCutoff(rules: CopyRules) {
  const now = new Date()
  if (rules.age === '6m') now.setMonth(now.getMonth() - 6)
  if (rules.age === '1y') now.setFullYear(now.getFullYear() - 1)
  if (rules.age === '2y') now.setFullYear(now.getFullYear() - 2)
  if (rules.age === '5y') now.setFullYear(now.getFullYear() - 5)
  return rules.age === 'all' || rules.age === 'custom' ? undefined : now
}

export function fileMatchesRules(file: DriveItem, rules: CopyRules) {
  if (file.mimeType === `${GOOGLE_MIME}folder` || file.mimeType === `${GOOGLE_MIME}shortcut`) return false
  if (!rules.includeTrash && file.trashed) return false
  if (rules.ownership === 'mine' && !file.ownedByMe) return false
  if (rules.ownership === 'shared' && !file.sharedWithMeTime) return false
  if (rules.folderId && !file.parents?.includes(rules.folderId)) return false
  if (!rules.categories[categoryFor(file)]) return false
  const hasKnownSize = file.size !== undefined
  const bytes = Number(file.size ?? 0)
  if ((rules.minSizeMb || rules.maxSizeMb) && !hasKnownSize) return false
  if (rules.minSizeMb && bytes < rules.minSizeMb * 1024 * 1024) return false
  if (rules.maxSizeMb && bytes > rules.maxSizeMb * 1024 * 1024) return false
  const date = file.modifiedTime ? new Date(file.modifiedTime) : undefined
  const cutoff = ageCutoff(rules)
  if (cutoff && date && date > cutoff) return false
  if (rules.age === 'custom' && rules.after && date && date < new Date(rules.after)) return false
  if (rules.age === 'custom' && rules.before && date && date > new Date(`${rules.before}T23:59:59`)) return false
  return true
}

export function humanRule(rules: CopyRules) {
  const activeCategories = Object.entries(rules.categories).filter(([, enabled]) => enabled).map(([category]) => category)
  const names: Record<string, string> = { images: 'fotos', videos: 'vídeos', audio: 'audio', documents: 'documentos', sheets: 'hojas', presentations: 'presentaciones', pdf: 'PDF', other: 'otros archivos' }
  const content = activeCategories.length === 8 ? 'todos los tipos de archivo' : activeCategories.map((category) => names[category]).join(', ') || 'ningún tipo seleccionado'
  const age: Record<CopyRules['age'], string> = { all: 'de cualquier antigüedad', '6m': 'de más de 6 meses', '1y': 'de más de 1 año', '2y': 'de más de 2 años', '5y': 'de más de 5 años', custom: 'en el intervalo elegido' }
  const ownership: Record<CopyRules['ownership'], string> = { all: 'propios y compartidos', mine: 'de tu propiedad', shared: 'compartidos contigo' }
  return `${content} · ${age[rules.age]} · ${ownership[rules.ownership]}`
}

export function formatBytes(bytes: number) {
  if (!Number.isFinite(bytes) || bytes <= 0) return '—'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let value = bytes
  let index = 0
  while (value >= 1024 && index < units.length - 1) { value /= 1024; index += 1 }
  return `${value.toLocaleString('es-ES', { maximumFractionDigits: value >= 100 || index === 0 ? 0 : 1 })} ${units[index]}`
}

export function formatDate(value?: string) {
  if (!value) return 'sin fecha'
  return new Intl.DateTimeFormat('es-ES', { year: 'numeric', month: 'short', day: 'numeric' }).format(new Date(value))
}
